// 표 칸 Ctrl+C / Ctrl+V (2026-09-23 오너 요청 "셀 내용 ctrl c v 돼야함").
//
// keydown 이 아니라 **copy/paste 이벤트**로 한다 — 클립보드를 권한 창 없이 읽고 쓰는 길은
// 이것뿐이고, 엑셀·구글 시트에서 복사한 표도 그대로 받는다. 칸을 고른 표가 있을 때만
// 가로채고, 다른 입력창(속성 패널 등)은 건드리지 않는다. 고른 칸의 입력창에서 글자를
// 끌어 골라 둔 복사, 한 줄 글자 붙여넣기는 평소대로 입력창이 받는다.
//
// 디자이너의 keydown 은 칸을 고른 표가 있으면 Ctrl+C/V 를 그냥 흘려보낸다(필드 복사가
// 가로채지 않게) — 그래서 브라우저가 이 copy/paste 이벤트를 낸다.

import { useEffect, useRef, type MutableRefObject } from 'react';
import type { LayoutTable } from '@bhops/shared';
import { clipFromText, copyCells, pasteCells, sameClipText, type CellClip } from './cell-clipboard';
import type { CellRange } from './useLayoutEditor';

/** 이 훅이 쓰는 편집기 부분(테스트에서 가짜로 넣기 쉽게 좁혔다). */
export interface CellClipboardEditor {
  selected: { kind: string } | null;
  cellRange: CellRange | null;
  replaceTable: (id: string, fn: (t: LayoutTable) => LayoutTable) => void;
  setCellRange: (r: CellRange | null) => void;
}

const isCellEditor = (el: Element | null): el is HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement && el.dataset.cellEditor === '1';

const isOtherInput = (el: Element | null) =>
  !isCellEditor(el) &&
  (el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    (el instanceof HTMLElement && el.isContentEditable));

export function useCellClipboard(
  editorRef: MutableRefObject<CellClipboardEditor>,
  notify: MutableRefObject<(msg: string) => void>,
): void {
  /** 이 화면에서 복사한 것 — 글자 모양·사진·입력 칸까지 붙인다. */
  const clipRef = useRef<CellClip | null>(null);

  useEffect(() => {
    function current() {
      const ed = editorRef.current;
      const t = ed.selected;
      if (!t || t.kind !== 'table' || !ed.cellRange) return null;
      const focus = document.activeElement;
      if (isOtherInput(focus)) return null;
      return { ed, t: t as LayoutTable, range: ed.cellRange, focus };
    }

    function onCopy(e: ClipboardEvent) {
      const cur = current();
      if (!cur) return;
      if (isCellEditor(cur.focus) && cur.focus.selectionStart !== cur.focus.selectionEnd) return;
      const clip = copyCells(cur.t, cur.range);
      e.clipboardData?.setData('text/plain', clip.tsv);
      e.preventDefault();
      clipRef.current = clip;
      const n = clip.cells.flat().filter(Boolean).length;
      notify.current(`칸 ${n}개를 복사했습니다. 붙일 칸을 고르고 Ctrl+V를 누르세요.`);
    }

    function onPaste(e: ClipboardEvent) {
      const cur = current();
      if (!cur) return;
      const text = e.clipboardData?.getData('text/plain') ?? '';
      const html = e.clipboardData?.getData('text/html') ?? '';
      const own =
        clipRef.current && sameClipText(clipRef.current.tsv, text) ? clipRef.current : null;
      // 엑셀·시트에서 온 표(탭, 또는 HTML 표)면 칸마다 나눠 넣는다.
      const looksGrid = text.includes('\t') || /<table/i.test(html);
      if (!own && isCellEditor(cur.focus) && !looksGrid) return; // 글자 붙여넣기 — 입력창이 받는다
      if (!own && !text) return;
      const { table, pasted } = pasteCells(cur.t, cur.range, own ?? clipFromText(text), !own);
      if (!pasted) return;
      e.preventDefault();
      cur.ed.replaceTable(cur.t.id, () => table);
      cur.ed.setCellRange(pasted);
    }

    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
    };
  }, [editorRef, notify]);
}
